import { useEffect, useState } from "react";
import { NavLink, Outlet, useLocation, useNavigate, Link } from "react-router-dom";
import {
    LayoutDashboard,
    ClipboardList,
    Printer,
    BarChart3,
    SlidersHorizontal,
    LogOut,
    Menu,
    X,
    UserRound,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";

const navItems = [
    { to: "/", label: "Dashboard", icon: LayoutDashboard, end: true, testid: "nav-dashboard" },
    { to: "/bestellingen", label: "Bestellingen", icon: ClipboardList, testid: "nav-orders" },
    { to: "/printcentrum", label: "Printcentrum", icon: Printer, testid: "nav-printcenter" },
    { to: "/verkoop", label: "Verkoop", icon: BarChart3, testid: "nav-sales" },
    { to: "/beheer", label: "Beheer", icon: SlidersHorizontal, testid: "nav-beheer" },
];

const ROLE_LABELS = {
    super_admin: "Super-admin",
    tenant_admin: "Beheerder",
    member: "Medewerker",
};

export default function AppShell() {
    const { user, tenant, logout } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const [open, setOpen] = useState(false);

    // Sluit drawer bij navigatie
    useEffect(() => {
        setOpen(false);
    }, [location.pathname]);

    const handleLogout = async () => {
        const loggedOut = await logout();
        if (loggedOut) {
            navigate("/login", { replace: true });
        } else {
            toast.error("Uitloggen is niet gelukt. Controleer je verbinding en probeer opnieuw.");
        }
    };

    return (
        <div className="min-h-screen flex bg-[#f4f6f3]">
            {/* Mobile header — alleen op kleine schermen */}
            <header
                className="lg:hidden fixed top-0 inset-x-0 h-16 z-30 bg-white/90 backdrop-blur-xl border-b border-florefy-900/10 flex items-center justify-between px-4 shadow-[0_1px_12px_rgba(23,61,44,0.06)]"
                data-testid="mobile-header"
            >
                <Link to="/" className="flex items-center gap-3" aria-label="Naar dashboard">
                    <img src="/florefy-logo.png" alt="Florefy Flow" className="h-8 w-auto object-contain" />
                    <span className="h-5 w-px bg-ink-200" />
                    <span className="text-[10px] font-semibold uppercase tracking-[0.16em] text-ink-500">Workspace</span>
                </Link>
                <button
                    onClick={() => setOpen((o) => !o)}
                    aria-label="Open menu"
                    data-testid="mobile-menu-toggle"
                    className="p-2.5 -mr-2 rounded-xl text-ink-700 hover:bg-florefy-50 transition-colors"
                >
                    {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
                </button>
            </header>

            {/* Overlay bij open drawer (mobiel) */}
            {open && (
                <div
                    className="lg:hidden fixed inset-0 bg-florefy-950/55 backdrop-blur-sm z-30"
                    onClick={() => setOpen(false)}
                    data-testid="mobile-overlay"
                />
            )}

            {/* Sidebar / Drawer */}
            <aside
                className={`app-shell-nav fixed top-0 left-0 z-40 h-screen w-72 shrink-0 bg-florefy-950 text-white flex flex-col transform transition-transform duration-300 ease-out shadow-[20px_0_60px_rgba(11,36,25,0.08)] ${
                    open ? "translate-x-0" : "-translate-x-full"
                } lg:translate-x-0`}
                data-testid="app-sidebar"
            >
                <div className="px-5 pt-5 pb-4 flex items-center justify-between">
                    <Link
                        to="/"
                        className="h-[68px] flex-1 rounded-2xl bg-white flex items-center justify-center px-5 shadow-[0_10px_30px_rgba(0,0,0,0.14)]"
                        aria-label="Naar dashboard"
                    >
                        <img
                            src="/florefy-logo.png"
                            alt="Florefy Flow"
                            className="w-full max-w-[178px] h-auto object-contain"
                            data-testid="florefy-logo"
                        />
                    </Link>
                    <button
                        onClick={() => setOpen(false)}
                        className="lg:hidden p-2 ml-2 text-white/60 hover:text-white hover:bg-white/10 rounded-xl"
                        aria-label="Sluit menu"
                    >
                        <X className="h-5 w-5" />
                    </button>
                </div>
                <nav className="flex-1 px-4 pt-6 pb-4 space-y-1.5 overflow-y-auto">
                    <div className="px-3 mb-3 text-[10px] font-semibold uppercase tracking-[0.2em] text-white/35">
                        Werkruimte
                    </div>
                    {navItems.map(({ to, label, icon: Icon, end, testid, desktopOnly }) => (
                        <NavLink
                            key={to}
                            to={to}
                            end={end}
                            data-testid={testid}
                            className={({ isActive }) =>
                                `group flex items-center gap-3.5 px-3.5 py-3 rounded-xl text-sm transition-all duration-150 ${
                                    desktopOnly ? "hidden lg:flex" : ""
                                } ${
                                    isActive
                                        ? "bg-white text-florefy-950 font-semibold shadow-[0_8px_24px_rgba(0,0,0,0.16)]"
                                        : "text-white/65 hover:text-white hover:bg-white/[0.07]"
                                }`
                            }
                        >
                            <span className="h-8 w-8 -ml-1 rounded-lg flex items-center justify-center bg-current/[0.06] group-hover:bg-current/[0.08] transition-colors">
                                <Icon className="h-[18px] w-[18px] shrink-0" />
                            </span>
                            {label}
                        </NavLink>
                    ))}
                </nav>
                <div className="p-4 border-t border-white/10 space-y-3 bg-black/[0.08]">
                    {user && (
                        <Link
                            to="/profiel"
                            className="flex items-center gap-3 hover:bg-white/[0.08] p-2.5 rounded-xl transition-colors border border-transparent hover:border-white/10"
                            data-testid="current-user"
                            title="Mijn account & wachtwoord wijzigen"
                        >
                            <div className="h-10 w-10 rounded-xl bg-florefy-300/20 border border-florefy-300/20 flex items-center justify-center shrink-0">
                                <UserRound className="h-5 w-5 text-florefy-200" />
                            </div>
                            <div className="text-xs leading-tight space-y-1 min-w-0 flex-1">
                                {tenant && (
                                    <div
                                        className="text-[9px] uppercase tracking-[0.16em] font-semibold text-florefy-200/70 truncate"
                                        data-testid="current-tenant"
                                        title={tenant.name}
                                    >
                                        {tenant.name}
                                    </div>
                                )}
                                <div className="font-semibold text-white truncate">{user.name || user.email}</div>
                                <div className="text-[9px] uppercase tracking-[0.14em] text-white/40">
                                    {ROLE_LABELS[user.role] || user.role}
                                </div>
                            </div>
                        </Link>
                    )}
                    <button
                        onClick={handleLogout}
                        className="w-full inline-flex items-center justify-center gap-2 px-3 py-2.5 text-xs font-medium border border-white/10 rounded-xl text-white/55 hover:text-white hover:bg-white/[0.07] transition-colors"
                        data-testid="logout-btn"
                        aria-label="Uitloggen"
                    >
                        <LogOut className="h-3.5 w-3.5" />
                        Uitloggen
                    </button>
                    <div className="text-[9px] uppercase tracking-[0.18em] text-white/20 text-center pt-1">
                        Florefy Flow · v1.22
                    </div>
                </div>
            </aside>
            <main className="app-content flex-1 min-w-0 pt-16 lg:pt-0 lg:ml-72">
                <Outlet />
            </main>
        </div>
    );
}
